//@flow

import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import fixEmailImage from 'app/common/images/fix_email.png';
import i18n from 'app/common/lib/i18n';
import resourceFactory from 'app/common/lib/ResourceFactory';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import { Experiment } from 'app/common/react/components/experiments';
import {
  Required,
  ContainsOnlyAscii,
  NewPassword,
  EqualTo,
  type Validator,
} from 'app/common/lib/react/validator';
import { Box, Button, Image, IconButton, Label, Text, TextField } from 'gestalt';

const STEP = {
  FIRST: 'first',
  END: 'end',
};

type Props = {|
  currentEmail?: string,
  username?: string,
  handleComplete: () => void,
  handleDismiss: () => void,
|};

type State = {|
  buttonLoading: boolean,
  currentStep: string,
  currentEmail?: string,
  newPassword: string,
  repeatPassword: string,
  newPasswordErrors: Array<string>,
  repeatPasswordErrors: Array<string>,
|};

type ValidationResult = {|
  errors: Array<string>,
  score?: number,
|};

const headerTextStyle = {
  __style: {
    width: '382px',
    height: '112px',
    fontFamily: 'HelveticaNeue',
    fontSize: '48px',
    fontWeight: 'bold',
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: '1.17',
    color: '#333333',
  },
};

export default class PasswordUpsellModal extends Component<Props, State> {
  state = {
    buttonLoading: false,
    currentStep: STEP.FIRST,
    currentEmail: this.props.currentEmail,
    newPassword: '',
    repeatPassword: '',
    newPasswordErrors: [],
    repeatPasswordErrors: [],
  };

  getModalText(currentStep: string) {
    if (currentStep === STEP.FIRST) {
      return i18n._(
        "Oh, no! You haven't set up your password yet.",
        'prompt to upsell social users to setup their password',
      );
    } else {
      return i18n._(
        'You are all set! Now you can login in using your email and password.',
        'message to show after user successfully setup  his/her password',
      );
    }
  }

  getFocusFieldRef = (c: ?TextField) => {
    const textFieldInstance = findDOMNode(c);
    if (textFieldInstance && textFieldInstance instanceof HTMLElement) {
      const inputElem = textFieldInstance.querySelector('input');
      if (inputElem) {
        inputElem.focus();
      }
    }
  };

  handleCancel = (waitTime: number) => {
    // wait time here used as a flag to deduct experiment group
    trackRegisterAction(`unauth_web.password_upsell_modal.submit.${waitTime}`);
    this.props.handleDismiss();
  };

  handleSubmit = (waitTime: number) => {
    const { handleComplete } = this.props;
    const { newPassword, newPasswordErrors, repeatPassword, repeatPasswordErrors } = this.state;

    this.handleButtonLoading(true);
    this.handleBlankFields();

    // Doesn't submit the form when there are client side errors
    if (
      newPasswordErrors.length ||
      repeatPasswordErrors.length ||
      newPassword === '' ||
      repeatPassword === ''
    ) {
      this.handleButtonLoading(false);
      return;
    }

    const resourceOptions = {
      new_password: newPassword,
      new_password_confirm: repeatPassword,
    };

    trackRegisterAction(`unauth_web.password_upsell_modal.submit.${waitTime}`);

    const resource = resourceFactory.create('UserPasswordResource', resourceOptions);
    resource.callUpdate({ showError: false }).then(
      () => {
        this.setState({ currentStep: STEP.END });
        setTimeout(handleComplete, waitTime);
      },
      // Should only occur when user enters an incorrect old password
      (denzelError) => {
        trackRegisterAction(
          `unauth_web.password_upsell_modal.update_error.${denzelError.api_error_code}`,
        );
        this.setState({ newPasswordErrors: [denzelError.message] });
        this.handleButtonLoading(false);
      },
    );
  };

  handleButtonLoading(buttonLoading: boolean) {
    this.setState({ buttonLoading });
  }

  // Sets error messages for blank fields upon submit for the case where
  // the form is submitted in an untouched state
  handleBlankFields = () => {
    const errors = {};
    if (this.state.newPassword === '') {
      errors.newPasswordErrors = [i18n._('Required', 'Error message to hint user missed a field.')];
    }
    if (this.state.repeatPassword === '') {
      errors.repeatPasswordErrors = [
        i18n._('Required', 'Error message to hint user missed a field.'),
      ];
    }
    this.setState(errors);
  };

  runValidators = (validators: Array<Validator>, value: string): ValidationResult => {
    let errors = [];
    let scoreObj = {};

    validators.forEach((validator) => {
      const validatorResults = validator(value);
      if (validatorResults && typeof validatorResults.score === 'number') {
        scoreObj = { score: validatorResults.score };
      }

      errors = errors.concat(validatorResults.errors);
    });

    return {
      errors,
      ...scoreObj,
    };
  };

  updateAndValidate = (password: string, value: string) => {
    const passwords = {
      newPassword: this.state.newPassword,
      repeatPassword: this.state.repeatPassword,
    };

    passwords[password] = value;

    let newPasswordErrors = [];
    let repeatPasswordErrors = [];

    if (passwords.newPassword !== '') {
      newPasswordErrors = this.validateNewPassword(passwords.newPassword).errors;
    }

    if (passwords.repeatPassword !== '' && passwords.newPassword !== '') {
      repeatPasswordErrors = this.validateRepeatPassword(
        passwords.newPassword,
        passwords.repeatPassword,
      ).errors;
    }

    this.setState({
      [password]: value,
      newPasswordErrors,
      repeatPasswordErrors,
    });
  };

  validateNewPassword(newPassword: string) {
    const validators = [
      Required(),
      ContainsOnlyAscii({ message: i18n.commonStrings.asciiPasswordError }),
      NewPassword({
        username: this.props.username,
        samePasswordStr: i18n._(
          'Oops! Your password shouldn’t match your username.',
          'Error message when user set his/her user name as his/her password ',
        ),
      }),
    ];

    return this.runValidators(validators, newPassword);
  }

  validateRepeatPassword(newPassword: string, repeatPassword: string) {
    const validators = [
      Required(),
      EqualTo({
        targetValue: newPassword,
        message: i18n._(
          'Passwords don’t match',
          'Error message when the password and typed password are different',
        ),
      }),
    ];

    return this.runValidators(validators, repeatPassword);
  }

  render() {
    const {
      currentEmail,
      currentStep,
      newPassword,
      repeatPassword,
      newPasswordErrors,
      repeatPasswordErrors,
    } = this.state;

    const submittable =
      newPassword &&
      repeatPassword &&
      newPasswordErrors.length === 0 &&
      repeatPasswordErrors.length === 0;

    return (
      <Experiment name="upsell_social_user_set_password" activate={false}>
        {({ group }) => {
          if (group.startsWith('enabled_var1')) {
            return (
              <Box
                display="flex"
                direction="column"
                color="white"
                overflow="hidden"
                shape="rounded"
                width={660}
                height={540}
              >
                <Box display="flex" alignItems="center">
                  <Box
                    display="flex"
                    alignItems="end"
                    justifyContent="center"
                    width={330}
                    height={540}
                  >
                    <Box width={292} height={479} marginBottom={-6}>
                      <Image
                        alt={i18n._('preview of mobile app', 'prompt to setup password')}
                        naturalWidth={292}
                        naturalHeight={479}
                        src={fixEmailImage}
                      />
                    </Box>
                  </Box>
                  <Box position="relative">
                    <Box display="flex" alignItems="center" width={330} height={540}>
                      <Box paddingY={6} marginTop={5}>
                        <Text overflow="normal" size="xl" weight="bold">
                          {this.getModalText(currentStep)}
                        </Text>
                        {currentStep === STEP.FIRST && (
                          <Box>
                            <Box>
                              <Box display="flex" marginTop={6}>
                                <Box width={80} display="flex" alignItems="center">
                                  <Label htmlFor="changePasswordNew">
                                    <Text weight="bold">
                                      {i18n._('Email', 'label for email field')}
                                    </Text>
                                  </Label>
                                </Box>
                                <Text>{currentEmail}</Text>
                              </Box>
                              <Box display="flex" marginTop={3}>
                                <Box width={80} display="flex" alignItems="center">
                                  <Label htmlFor="changePasswordNew">
                                    <Text weight="bold">
                                      {i18n._('Password', 'label for new password field')}
                                    </Text>
                                  </Label>
                                </Box>
                                <Box width={225}>
                                  <TextField
                                    ref={this.getFocusFieldRef}
                                    errorMessage={newPasswordErrors[0]}
                                    id="changePasswordNew"
                                    name="new"
                                    onChange={({ value }) =>
                                      this.updateAndValidate('newPassword', value)
                                    }
                                    type="password"
                                    value={newPassword}
                                  />
                                </Box>
                              </Box>
                              <Box display="flex" marginTop={3}>
                                <Box width={80} display="flex" alignItems="center">
                                  <Label htmlFor="changePasswordRetype">
                                    <Text weight="bold">
                                      {i18n._('Retype', 'label for retype password field')}
                                    </Text>
                                  </Label>
                                </Box>
                                <Box width={225}>
                                  <TextField
                                    errorMessage={repeatPasswordErrors[0]}
                                    id="changePasswordRetype"
                                    name="new_confirm"
                                    onChange={({ value }) =>
                                      this.updateAndValidate('repeatPassword', value)
                                    }
                                    type="password"
                                    value={repeatPassword}
                                  />
                                </Box>
                              </Box>
                            </Box>
                            <Box
                              display="flex"
                              justifyContent="between"
                              marginTop={5}
                              marginRight={6}
                            >
                              <Button
                                color="red"
                                type="submit"
                                disabled={!submittable || this.state.buttonLoading}
                                onClick={() => this.handleSubmit(2500)}
                                text={i18n._(
                                  'Set Password',
                                  'Label text for button that will set password.',
                                )}
                                size="md"
                              />
                            </Box>
                            <Box
                              display="flex"
                              justifyContent="between"
                              marginTop={3}
                              marginRight={6}
                            >
                              <Button
                                onClick={() => this.handleCancel(2500)}
                                text={i18n._(
                                  'Cancel',
                                  'Label text for button that will set password.',
                                )}
                                size="md"
                              />
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          } else {
            return (
              <Box
                display="flex"
                direction="column"
                color="white"
                overflow="hidden"
                shape="rounded"
                padding={4}
                width={414}
                height={436}
              >
                <Box display="flex">
                  <Box dangerouslySetInlineStyle={headerTextStyle}>
                    {i18n._(
                      'You need a password',
                      'Header text for password upsell for social users.',
                    )}
                  </Box>
                  <Box>
                    <IconButton
                      accessibilityLabel={i18n._('Close', 'accessible label for close button')}
                      iconColor="gray"
                      icon="cancel"
                      onClick={() => this.handleCancel(0)}
                      size="xl"
                    />
                  </Box>
                </Box>
                <Box marginTop={8}>
                  <Text overflow="normal" size="lg">
                    {i18n._(
                      "Oh no! Looks like you haven't created a password for this account yet." +
                        ' Set it now to log in with email and password in the future.',
                      'Explanatory text to upsell social users to setup their password',
                    )}
                  </Text>
                  <Box marginTop={3}>
                    <Text size="lg" weight="bold">
                      {currentEmail}
                    </Text>
                  </Box>
                  <Box marginTop={2}>
                    <TextField
                      ref={this.getFocusFieldRef}
                      errorMessage={newPasswordErrors[0]}
                      id="changePasswordNew"
                      name="new"
                      onChange={({ value }) => this.updateAndValidate('newPassword', value)}
                      placeholder={i18n._(
                        'Enter Password',
                        'placeholder text for the password textfield',
                      )}
                      type="password"
                      value={newPassword}
                    />
                  </Box>
                  <Box marginTop={2}>
                    <TextField
                      errorMessage={repeatPasswordErrors[0]}
                      id="changePasswordRetype"
                      name="new_confirm"
                      onChange={({ value }) => this.updateAndValidate('repeatPassword', value)}
                      placeholder={i18n._(
                        'Re-enter Password',
                        'placeholder text for the retype password textfield',
                      )}
                      type="password"
                      value={repeatPassword}
                    />
                  </Box>
                </Box>
                <Box display="flex" justifyContent="between" marginTop={2}>
                  <Button
                    color="red"
                    type="submit"
                    disabled={!submittable || this.state.buttonLoading}
                    onClick={() => this.handleSubmit(0)}
                    text={i18n._(
                      'Create Password',
                      'Label text for button that will set password.',
                    )}
                    size="lg"
                  />
                </Box>
              </Box>
            );
          }
        }}
      </Experiment>
    );
  }
}
